import React, { useState } from 'react';
import { UploadCloud, Loader2, Download, Trash2, Eye, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { dematAPI } from '../../services/api';

const FIELD_LABELS = { isin: 'ISIN', units: 'Units', pan: 'PAN', name: 'Investor Name', bank_account: 'Bank A/c No', ifsc: 'IFSC' };
const err = (e) => e?.response?.data?.detail || e.message;

const MappingGrid = ({ parsed, mapping, setMapping }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="benpos-mapping-grid">
    {parsed.fields.map((f) => (
      <div key={f}>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {FIELD_LABELS[f]} {parsed.required.includes(f) && <span className="text-red-500">*</span>}
        </label>
        <select value={mapping[f] || ''} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })}
          className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${parsed.required.includes(f) && !mapping[f] ? 'border-red-300' : ''}`}
          data-testid={`benpos-map-${f}`}>
          <option value="">-- Not mapped --</option>
          {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
      </div>
    ))}
  </div>
);

const SamplePreview = ({ parsed, mapping }) => (
  <div className="overflow-x-auto border rounded-lg mt-4">
    <table className="w-full text-xs" data-testid="benpos-sample-table">
      <thead><tr className="bg-gray-50 text-gray-600">{parsed.fields.filter((f) => mapping[f]).map((f) => <th key={f} className="px-3 py-2 text-left">{FIELD_LABELS[f]} <span className="text-gray-400 font-normal">← {mapping[f]}</span></th>)}</tr></thead>
      <tbody>
        {parsed.sample_rows.map((r, i) => (
          <tr key={i} className="border-t">{parsed.fields.filter((f) => mapping[f]).map((f) => <td key={f} className="px-3 py-1.5">{r[mapping[f]] || '—'}</td>)}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

const UploadHistory = ({ uploads, onOpen, onChanged }) => {
  const download = async (u) => {
    try {
      const res = await dematAPI.downloadFile(u.id);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = u.file_name || 'benpos'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(`Download failed: ${err(e)}`); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete BenPos snapshot as on ${u.as_on_date}?`)) return;
    try { await dematAPI.remove(u.id); toast.success('Snapshot deleted'); onChanged(); } catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="bg-white border rounded-lg" data-testid="benpos-history">
      <div className="px-4 py-3 border-b font-semibold text-gray-800 text-sm">Uploaded Snapshots ({uploads.length})</div>
      {!uploads.length ? <p className="p-6 text-sm text-gray-400 text-center">No BenPos reports uploaded for this fund yet.</p> : (
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50 text-left text-gray-600"><th className="px-4 py-2">As on</th><th className="px-4 py-2">File</th><th className="px-4 py-2 text-right">Rows</th><th className="px-4 py-2 text-right">Total Units</th><th className="px-4 py-2">Uploaded</th><th className="px-4 py-2" /></tr></thead>
          <tbody>
            {uploads.map((u) => (
              <tr key={u.id} className="border-t" data-testid={`benpos-upload-row-${u.id}`}>
                <td className="px-4 py-2 font-medium">{u.as_on_date}</td>
                <td className="px-4 py-2">{u.file_name}{u.remarks && <span className="block text-xs text-gray-400">{u.remarks}</span>}</td>
                <td className="px-4 py-2 text-right tabular-nums">{u.row_count}{u.skipped_rows ? <span className="text-xs text-amber-600"> (+{u.skipped_rows} skipped)</span> : null}</td>
                <td className="px-4 py-2 text-right tabular-nums">{Number(u.total_units).toLocaleString('en-IN')}</td>
                <td className="px-4 py-2 text-xs text-gray-500">{u.uploaded_by}<br />{new Date(u.created_at).toLocaleString()}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => onOpen(u.id)} className="p-1.5 text-teal-700 hover:bg-teal-50 rounded" title="Reconcile" data-testid={`benpos-open-${u.id}`}><Eye size={15} /></button>
                  <button onClick={() => download(u)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title="Download original" data-testid={`benpos-download-${u.id}`}><Download size={15} /></button>
                  <button onClick={() => remove(u)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete" data-testid={`benpos-delete-${u.id}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export const BenposUploadTab = ({ fundId, uploads, onUploaded, onChanged, onOpen }) => {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);

  const pick = async (f) => {
    if (!f) return;
    setFile(f); setParsed(null); setBusy(true);
    try {
      const { data } = await dematAPI.parse(f);
      setParsed(data); setMapping(data.suggested_mapping || {});
    } catch (e) { toast.error(err(e)); setFile(null); } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await dematAPI.upload({ file, fundId, asOnDate, mapping, remarks });
      toast.success(`BenPos uploaded — ${data.row_count} rows`);
      setFile(null); setParsed(null); setRemarks('');
      onUploaded(data);
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  const missing = parsed ? parsed.required.filter((f) => !mapping[f]) : [];

  return (
    <div className="space-y-6">
      <div className="bg-white border rounded-lg p-5" data-testid="benpos-upload-card">
        <h2 className="font-semibold text-gray-900 mb-1">Upload BenPos Report</h2>
        <p className="text-xs text-gray-500 mb-4">CSV, TXT (comma / pipe / tab delimited) or XLSX. First row must contain column headers. Each upload is saved as a dated snapshot.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="md:col-span-1 flex flex-col items-center justify-center border-2 border-dashed border-teal-300 rounded-lg p-5 cursor-pointer hover:bg-teal-50/40 text-center">
            {busy && !parsed ? <Loader2 className="animate-spin text-teal-600" /> : file ? <CheckCircle2 className="text-teal-600" /> : <UploadCloud className="text-teal-600" />}
            <span className="text-sm mt-2 text-gray-700 break-all" data-testid="benpos-file-name">{file ? file.name : 'Choose BenPos file'}</span>
            <input type="file" accept=".csv,.txt,.xlsx,.xlsm" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ''; }} data-testid="benpos-file-input" />
          </label>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">As-on Date <span className="text-red-500">*</span></label>
            <input type="date" value={asOnDate} onChange={(e) => setAsOnDate(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="benpos-as-on-date" />
            <p className="text-[11px] text-gray-400 mt-1">Portal units are computed from transactions up to this date.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
            <input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="e.g. NSDL BenPos Q3" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="benpos-remarks" />
          </div>
        </div>

        {parsed && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">Map Columns <span className="font-normal text-gray-400">({parsed.row_count} rows detected — mapping is auto-detected and remembered)</span></h3>
            </div>
            <MappingGrid parsed={parsed} mapping={mapping} setMapping={setMapping} />
            <SamplePreview parsed={parsed} mapping={mapping} />
            <div className="flex justify-end items-center gap-3 mt-4">
              {missing.length > 0 && <span className="text-xs text-red-600" data-testid="benpos-missing-required">Map required: {missing.map((f) => FIELD_LABELS[f]).join(', ')}</span>}
              <button onClick={submit} disabled={busy || missing.length > 0 || !asOnDate} data-testid="benpos-submit-btn"
                className="inline-flex items-center px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
                {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <UploadCloud size={14} className="mr-1.5" />} Upload & Reconcile
              </button>
            </div>
          </div>
        )}
      </div>
      <UploadHistory uploads={uploads} onOpen={onOpen} onChanged={onChanged} />
    </div>
  );
};
